const defaultDateCityStyle = {
  top: "2%",
  left: "3%",
  width: "auto",
  fontSize: "14px",
  fontFamily: "Georgia, serif",
  color: "#FFFFFF",
  textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
};

const defaultPlaceNameStyle = {
  top: "100%",
  marginTop: "2px",
  left: "0%",
  width: "100%",
  textAlign: "center",
  fontSize: "10px",
  fontFamily: "Arial, sans-serif",
  color: "#333333",
  lineHeight: "1.2", 
};

const defaultPageNumStyle = {
  bottom: "2%",
  fontSize: "12px",
  fontFamily: "Arial, sans-serif",
  color: "#FFFFFF",
  textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
};

const defaultScatteredWordStyle = {
  fontSize: "18px",
  fontFamily: "Brush Script MT, cursive",
  color: "#4A4A4A",
  padding: "5px",
  textShadow: "1px 1px 1px rgba(0,0,0,0.1)",
  width: "auto",
  height: "auto",
  textAlign: "center"
};

// Map of default styles
const defaultStyles = {
  defaultDateCityStyle,
  defaultPlaceNameStyle,
  defaultPageNumStyle,
  defaultScatteredWordStyle
};

// Function to process a style object and replace 'default' key with actual default style
const processStyles = (styleObj) => {
  if (!styleObj) return styleObj;
  
  if (styleObj.default) {
    const defaultStyleName = styleObj.default;
    const defaultStyle = defaultStyles[defaultStyleName];
    // Remove the default key and merge with the default style
    const { default: _, ...rest } = styleObj;
    return { ...defaultStyle, ...rest };
  }
  
  // Process nested style objects
  const result = {};
  for (const key in styleObj) {
    if (typeof styleObj[key] === 'object' && styleObj[key] !== null) {
      result[key] = processStyles(styleObj[key]);
    } else {
      result[key] = styleObj[key];
    }
  }
  
  return result;
};

// Process an entire layout to replace all default styles
const processLayout = (layout) => {
  const result = { ...layout };
  
  // Process left page
  if (layout.leftPage) {
    result.leftPage = { ...layout.leftPage };
    
    // Process placeholders with styles
    ['placeName', 'dateCityLocation', 'pageNumberLocation'].forEach(key => {
      if (result.leftPage[key]?.style) {
        result.leftPage[key] = {
          ...result.leftPage[key],
          style: processStyles(result.leftPage[key].style)
        };
      }
    });
    
    // Process text style if present
    if (result.leftPage.text?.style) {
      result.leftPage.text = {
        ...result.leftPage.text,
        style: processStyles(result.leftPage.text.style)
      };
    }
    
    // Process scattered word placeholders
    if (result.leftPage.scatteredWordPlaceholders) {
      result.leftPage.scatteredWordPlaceholders = result.leftPage.scatteredWordPlaceholders.map(
        placeholder => ({
          ...placeholder,
          style: processStyles(placeholder.style)
        })
      );
    }
  }
  
  // Process right page with the same approach
  if (layout.rightPage) {
    result.rightPage = { ...layout.rightPage };
    
    ['placeName', 'dateCityLocation', 'pageNumberLocation'].forEach(key => {
      if (result.rightPage[key]?.style) {
        result.rightPage[key] = {
          ...result.rightPage[key],
          style: processStyles(result.rightPage[key].style)
        };
      }
    });
    
    if (result.rightPage.text?.style) {
      result.rightPage.text = {
        ...result.rightPage.text,
        style: processStyles(result.rightPage.text.style)
      };
    }
    
    if (result.rightPage.scatteredWordPlaceholders) {
      result.rightPage.scatteredWordPlaceholders = result.rightPage.scatteredWordPlaceholders.map(
        placeholder => ({
          ...placeholder,
          style: processStyles(placeholder.style)
        })
      );
    }
  }
  
  return result;
};

// Load layouts from JSON file
const loadLayouts = async () => {
  try {
    const response = await fetch('/assets/layouts.json');
    if (!response.ok) {
      throw new Error(`Failed to load layouts: ${response.status}`);
    }
    const layoutsData = await response.json();
    
    // Process all layouts to replace 'default' references with actual styles
    return layoutsData.map(layout => processLayout(layout));
  } catch (error) {
    console.error('Error loading layouts:', error);
    return [];
  }
};

// Export the layouts array that gets populated with loaded data
export const layouts = [];

// Export the default styles and loadLayouts function
export { defaultDateCityStyle, defaultPlaceNameStyle, defaultPageNumStyle, defaultScatteredWordStyle };

// Function to find if a style object matches one of the default styles
export const findMatchingDefaultStyle = (styleObject) => {
  if (!styleObject || typeof styleObject !== 'object' || Object.keys(styleObject).length === 0) {
    return { defaultName: null, remainingStyle: styleObject || {} };
  }

  let bestMatch = {
    defaultName: null,
    remainingStyle: { ...styleObject },
    // Count of properties in the original styleObject that would NOT be covered by a default
    // Initialize with all properties, assuming no default match.
    unexplainedKeyCount: Object.keys(styleObject).length 
  };

  for (const defaultName in defaultStyles) {
    const currentDefaultStyle = defaultStyles[defaultName];
    let allKeysFromDefaultAreInStyleObject = true;

    // Check 1: styleObject must contain all keys that are in currentDefaultStyle
    // (Their values can be different; those would be overrides)
    for (const keyInDefault in currentDefaultStyle) {
      if (styleObject[keyInDefault] === undefined) {
        allKeysFromDefaultAreInStyleObject = false;
        break;
      }
    }

    if (allKeysFromDefaultAreInStyleObject) {
      // If all keys from the default are present, this default is a candidate.
      // Now, calculate the "remainingStyle" if this default were used.
      // remainingStyle = (properties in styleObject not in currentDefaultStyle) + 
      //                  (properties in styleObject that ARE in currentDefaultStyle but have different values)
      const currentRemainingStyle = {};
      let currentUnexplainedKeyCount = 0;

      for (const keyInStyleObject in styleObject) {
        if (currentDefaultStyle[keyInStyleObject] === undefined) {
          // This key from styleObject is not in the current default, so it's purely additional.
          currentRemainingStyle[keyInStyleObject] = styleObject[keyInStyleObject];
          currentUnexplainedKeyCount++;
        } else {
          // This key from styleObject IS also in currentDefaultStyle.
          // If values differ, it's an override and part of remainingStyle.
          if (styleObject[keyInStyleObject] !== currentDefaultStyle[keyInStyleObject]) {
            currentRemainingStyle[keyInStyleObject] = styleObject[keyInStyleObject];
            currentUnexplainedKeyCount++;
          }
          // If values are the same, it's covered by the default and not in remainingStyle.
        }
      }
      
      // This default (currentDefaultStyle) is a "better" match if applying it
      // results in fewer "unexplained" keys (overrides + additions)
      // than the previous best match.
      // We also add 1 to currentUnexplainedKeyCount to account for the 'default' key itself,
      // if we were comparing total resulting keys. But comparing unexplained keys is direct.
      if (currentUnexplainedKeyCount < bestMatch.unexplainedKeyCount) {
        bestMatch = {
          defaultName: defaultName,
          remainingStyle: currentRemainingStyle,
          unexplainedKeyCount: currentUnexplainedKeyCount
        };
      }
    }
  }

  // If a defaultName was found (i.e., bestMatch.defaultName is not null),
  // it means applying that default resulted in fewer unexplained keys than
  // just using the original styleObject.
  if (bestMatch.defaultName) {
    // Check if using this default is actually "simpler" than the original.
    // Simpler means: 1 (for 'default' key) + Object.keys(bestMatch.remainingStyle).length < Object.keys(styleObject).length
    // This is equivalent to: bestMatch.unexplainedKeyCount < Object.keys(styleObject).length,
    // which is implicitly true if bestMatch.unexplainedKeyCount was updated from its initial value.
    // The initial value of unexplainedKeyCount is Object.keys(styleObject).length.
    // So, if bestMatch.unexplainedKeyCount < Object.keys(styleObject).length, a simpler representation was found.
    if (bestMatch.unexplainedKeyCount < Object.keys(styleObject).length) {
        return { defaultName: bestMatch.defaultName, remainingStyle: bestMatch.remainingStyle || {} };
    }
  }
  
  // Fallback: No default provided a "simpler" representation, or no default was applicable.
  return { defaultName: null, remainingStyle: { ...styleObject } };
};

// Also export defaultStyles itself so the editor can access it
export { defaultStyles };

// Immediately load the layouts when this module is imported
// This ensures the layouts array is populated as soon as possible
loadLayouts().then(loadedLayouts => {
  // Clear the array and add all loaded layouts
  layouts.length = 0;
  layouts.push(...loadedLayouts);
  console.log(`Loaded ${loadedLayouts.length} layouts from JSON`);
}).catch(error => {
  console.error('Failed to load layouts:', error);
});

// Also export the loadLayouts function for cases where a manual reload is needed
// Export processLayout for use in LayoutEditor when loading JSON files
export { loadLayouts, processLayout };
